import type { OutOfBandTransactionStatus } from "@/modules/finances/boxPaymentAtCounter/entities/outOfBandTransactionType"
import type { StripeErrorCode } from "@/modules/finances/types/stripeErrorTypes"
import { type AddressDto } from "@modules/address/addressDto"
import { type PhoneNumberWithPrefix } from "@modules/box/boxTypes"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import {
  DiscountType,
  type TransactionBoxDetailsDto,
  type TransactionDetailHistoryDto,
  type TransactionDetailsDto,
  type TransactionDiscountDto,
  type TransactionLineDto,
  type TransactionPayoutDetailDto,
  type TransactionUserDetailsDto,
} from "@modules/finances/gateway/transactionDto"
import {
  DisputeReason,
  DisputeStatus,
  PaymentMethodType,
  TransactionStatus,
} from "@modules/finances/types/financesType"
import { type HUImage } from "@modules/types/imageTypes"

export class TransactionDiscountEntity {
  amount: number
  currency: string | null
  couponType: CouponType
  name: string | null
  type: DiscountType

  private constructor(transactionDiscount: TransactionDiscountEntity) {
    this.amount = transactionDiscount.amount
    this.currency = transactionDiscount.currency
    this.couponType = transactionDiscount.couponType
    this.name = transactionDiscount.name
    this.type = transactionDiscount.type
  }

  static fromDto(transactionDiscountDto: TransactionDiscountDto): TransactionDiscountEntity {
    return new TransactionDiscountEntity({
      amount: transactionDiscountDto.amount,
      currency: transactionDiscountDto.currency,
      couponType: transactionDiscountDto.couponType,
      name: transactionDiscountDto.name,
      type: transactionDiscountDto.type,
    })
  }
}

export class TransactionDetailHistoryEntity {
  date: Date
  status: TransactionStatus
  errorCode: StripeErrorCode | null

  private constructor(transactionDetailHistory: TransactionDetailHistoryEntity) {
    this.date = transactionDetailHistory.date
    this.status = transactionDetailHistory.status
    this.errorCode = transactionDetailHistory.errorCode
  }

  static fromDto(transactionDetailHistoryDto: TransactionDetailHistoryDto): TransactionDetailHistoryEntity {
    return new TransactionDetailHistoryEntity({
      date: new Date(transactionDetailHistoryDto.date),
      status: transactionDetailHistoryDto.status,
      errorCode: transactionDetailHistoryDto.errorCode,
    })
  }
}

export class TransactionPayoutDetailEntity {
  payoutId: string
  payoutAt: Date

  private constructor(transactionPayoutDetail: TransactionPayoutDetailEntity) {
    this.payoutId = transactionPayoutDetail.payoutId
    this.payoutAt = transactionPayoutDetail.payoutAt
  }

  static fromDto(transactionPayoutDetailDto: TransactionPayoutDetailDto): TransactionPayoutDetailEntity {
    return new TransactionPayoutDetailEntity({
      payoutId: transactionPayoutDetailDto.payoutId,
      payoutAt: new Date(transactionPayoutDetailDto.payoutAt),
    })
  }
}

export class TransactionLineEntity {
  description: string | null
  netAmount: number
  taxAmount: number | null
  totalAmount: number
  taxPercent: number | null
  quantity: number
  currency: string
  periodStart: Date | null
  periodEnd: Date | null

  private constructor(transactionLine: TransactionLineEntity) {
    this.description = transactionLine.description
    this.netAmount = transactionLine.netAmount
    this.taxAmount = transactionLine.taxAmount
    this.totalAmount = transactionLine.totalAmount
    this.taxPercent = transactionLine.taxPercent
    this.quantity = transactionLine.quantity
    this.currency = transactionLine.currency
    this.periodStart = transactionLine.periodStart
    this.periodEnd = transactionLine.periodEnd
  }

  static fromDto(transactionLineDto: TransactionLineDto): TransactionLineEntity {
    return new TransactionLineEntity({
      description: transactionLineDto.description,
      netAmount: transactionLineDto.netAmount,
      taxAmount: transactionLineDto.taxAmount,
      totalAmount: transactionLineDto.totalAmount,
      taxPercent: transactionLineDto.taxPercent,
      quantity: transactionLineDto.quantity,
      currency: transactionLineDto.currency,
      periodStart: transactionLineDto.periodStart ? new Date(transactionLineDto.periodStart) : null,
      periodEnd: transactionLineDto.periodEnd ? new Date(transactionLineDto.periodEnd) : null,
    })
  }
}

export class TransactionUserDetailsEntity {
  id: string
  boxMemberNumber: string | null
  firstname: string
  lastname: string
  username: string
  avatar: HUImage | null
  address: AddressDto | null
  email: string
  nationality: string | null
  phoneNumber: PhoneNumberWithPrefix | null
  stripeCustomerId: string

  private constructor(transactionUser: TransactionUserDetailsEntity) {
    this.id = transactionUser.id
    this.boxMemberNumber = transactionUser.boxMemberNumber
    this.firstname = transactionUser.firstname
    this.lastname = transactionUser.lastname
    this.username = transactionUser.username
    this.avatar = transactionUser.avatar
    this.address = transactionUser.address
    this.email = transactionUser.email
    this.nationality = transactionUser.nationality
    this.phoneNumber = transactionUser.phoneNumber
    this.stripeCustomerId = transactionUser.stripeCustomerId
  }

  static fromDto(transactionUserDto: TransactionUserDetailsDto): TransactionUserDetailsEntity {
    return new TransactionUserDetailsEntity({
      id: transactionUserDto.id,
      boxMemberNumber: transactionUserDto.boxMemberNumber,
      firstname: transactionUserDto.firstname,
      lastname: transactionUserDto.lastname,
      username: transactionUserDto.username,
      avatar: transactionUserDto.avatar ? convertImageFromDto(transactionUserDto.avatar) : null,
      address: transactionUserDto.address,
      email: transactionUserDto.email,
      nationality: transactionUserDto.nationality,
      phoneNumber: transactionUserDto.phoneNumber,
      stripeCustomerId: transactionUserDto.stripeCustomerId,
    })
  }
}

export class TransactionBoxDetailsEntity {
  id: string
  stripeAccountId: string
  name: string
  logo: HUImage
  email: string
  address: AddressDto | null
  phoneNumber: PhoneNumberWithPrefix | null
  nationality: string | null

  private constructor(transactionBox: TransactionBoxDetailsEntity) {
    this.id = transactionBox.id
    this.stripeAccountId = transactionBox.stripeAccountId
    this.name = transactionBox.name
    this.logo = transactionBox.logo
    this.email = transactionBox.email
    this.address = transactionBox.address
    this.phoneNumber = transactionBox.phoneNumber
    this.nationality = transactionBox.nationality
  }

  static fromDto(transactionBoxDto: TransactionBoxDetailsDto): TransactionBoxDetailsEntity {
    return new TransactionBoxDetailsEntity({
      id: transactionBoxDto.id,
      stripeAccountId: transactionBoxDto.stripeAccountId,
      name: transactionBoxDto.name,
      logo: transactionBoxDto.logo,
      email: transactionBoxDto.email,
      address: transactionBoxDto.address,
      phoneNumber: transactionBoxDto.phoneNumber,
      nationality: transactionBoxDto.nationality,
    })
  }
}

export class TransactionDetailsEntity {
  transactionId: string
  date: Date
  paidAt: Date | null
  user: TransactionUserDetailsEntity | null
  isAnonymous: boolean
  box: TransactionBoxDetailsEntity | null
  amount: number
  netAmount: number
  refundAmount: number
  totalTaxAmount: number
  fee: number
  currency: string
  status: TransactionStatus
  disputed: boolean
  refundable: boolean
  refunded: boolean
  invoiceNumber: string
  invoiceId: string | null
  invoicePdfUrl: string | null
  lines: TransactionLineEntity[]
  discounts: TransactionDiscountEntity[]
  paymentMethodDetail: {
    type: PaymentMethodType
    last4Digits: string | null
  } | null
  disputeDetail: {
    amount: number
    fee: number
    status: DisputeStatus
    reason: DisputeReason
  } | null
  history: TransactionDetailHistoryEntity[] | null
  outOfBandStatus: OutOfBandTransactionStatus | null
  payout: TransactionPayoutDetailEntity | null

  private constructor(transactionDetails: TransactionDetailsEntity) {
    this.transactionId = transactionDetails.transactionId
    this.paidAt = transactionDetails.paidAt
    this.isAnonymous = transactionDetails.isAnonymous
    this.refundable = transactionDetails.refundable
    this.date = transactionDetails.date
    this.amount = transactionDetails.amount
    this.fee = transactionDetails.fee
    this.netAmount = transactionDetails.netAmount
    this.currency = transactionDetails.currency
    this.status = transactionDetails.status
    this.disputed = transactionDetails.disputed
    this.refunded = transactionDetails.refunded
    this.refundAmount = transactionDetails.refundAmount
    this.totalTaxAmount = transactionDetails.totalTaxAmount
    this.invoiceId = transactionDetails.invoiceId
    this.invoiceNumber = transactionDetails.invoiceNumber
    this.invoicePdfUrl = transactionDetails.invoicePdfUrl
    this.lines = transactionDetails.lines
    this.discounts = transactionDetails.discounts
    this.user = transactionDetails.user
    this.box = transactionDetails.box
    this.paymentMethodDetail = transactionDetails.paymentMethodDetail
    this.disputeDetail = transactionDetails.disputeDetail
    this.history = transactionDetails.history
    this.outOfBandStatus = transactionDetails.outOfBandStatus
    this.payout = transactionDetails.payout
  }

  static fromDto(transactionDetailsDto: TransactionDetailsDto): TransactionDetailsEntity {
    return new TransactionDetailsEntity({
      transactionId: transactionDetailsDto.transactionId,
      paidAt: transactionDetailsDto.paidAt ? new Date(transactionDetailsDto.paidAt) : null,
      isAnonymous: transactionDetailsDto.isAnonymous,
      refundable: transactionDetailsDto.refundable,
      date: new Date(transactionDetailsDto.date),
      amount: transactionDetailsDto.amount,
      fee: transactionDetailsDto.fee,
      netAmount: transactionDetailsDto.netAmount,
      currency: transactionDetailsDto.currency,
      status: transactionDetailsDto.status,
      disputed: transactionDetailsDto.disputed,
      refunded: transactionDetailsDto.refunded,
      totalTaxAmount: transactionDetailsDto.totalTaxAmount,
      refundAmount: transactionDetailsDto.refundAmount,
      invoiceId: transactionDetailsDto.invoiceId,
      invoiceNumber: transactionDetailsDto.invoiceNumber,
      invoicePdfUrl: transactionDetailsDto.invoicePdfUrl,
      lines: transactionDetailsDto.lines.map((line) => TransactionLineEntity.fromDto(line)),
      discounts: transactionDetailsDto.discounts.map((discount) => TransactionDiscountEntity.fromDto(discount)),
      user: transactionDetailsDto.user ? TransactionUserDetailsEntity.fromDto(transactionDetailsDto.user) : null,
      box: transactionDetailsDto.box ? TransactionBoxDetailsEntity.fromDto(transactionDetailsDto.box) : null,
      paymentMethodDetail: transactionDetailsDto.paymentMethodDetail,
      disputeDetail: transactionDetailsDto.disputeDetail,
      history: transactionDetailsDto.history
        ? transactionDetailsDto.history
            .map((history) => TransactionDetailHistoryEntity.fromDto(history))
            .sort((a, b) => b.date.getTime() - a.date.getTime())
        : null,
      outOfBandStatus: transactionDetailsDto.outOfBandStatus,
      payout: transactionDetailsDto.payout ? TransactionPayoutDetailEntity.fromDto(transactionDetailsDto.payout) : null,
    })
  }
}
